import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle, 
  FileText,
  Plus,
  Loader2,
  AlertCircle,
  Search,
  X,
  Sparkles,
  Crown,
  Zap,
  ChevronDown,
  Circle,
  PlayCircle,
  HelpCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import ProductTour, { useTour } from "@/components/ProductTour";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  pending: "Pendiente",
  recording: "Grabando",
  transcribing: "Transcribiendo",
  processing: "Procesando",
  completed: "Completado",
  error: "Error"
};

const statusIcons = {
  pending: Clock,
  recording: AlertCircle,
  transcribing: Loader2,
  processing: Loader2,
  completed: CheckCircle,
  error: AlertCircle
};

const MeetingCard = ({ meeting, index }) => {
  const StatusIcon = statusIcons[meeting.status] || Clock;
  const isProcessing = ["transcribing", "processing"].includes(meeting.status);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <Link to={`/meeting/${meeting.id}`} data-testid={`meeting-card-${meeting.id}`}>
        <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-lg line-clamp-2 group-hover:text-orange-600 transition-colors">
                {meeting.title}
              </CardTitle>
              <Badge 
                className={`status-${meeting.status} shrink-0`}
                variant="secondary"
              >
                <StatusIcon className={`w-3 h-3 mr-1 ${isProcessing ? "animate-spin" : ""}`} />
                {statusLabels[meeting.status]}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {meeting.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {meeting.description}
              </p>
            )}
            
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {format(parseISO(meeting.date), "d MMM yyyy", { locale: es })}
              </span>
              {meeting.participants?.length > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {meeting.participants.length}
                </span>
              )}
              {meeting.action_items?.length > 0 && (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {meeting.action_items.filter(i => i.completed).length}/{meeting.action_items.length}
                </span>
              )}
            </div>
            
            {meeting.summary && (
              <p className="text-sm text-muted-foreground line-clamp-2 pt-2 border-t border-border">
                {meeting.summary.substring(0, 100)}...
              </p>
            )}
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

// Tasks Section Component - Collapsible
const TasksSection = ({ title, tasks, type, icon, emptyMessage, onTaskUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);

  const updateTaskStatus = async (task, newStatus) => {
    try {
      await axios.patch(
        `${API}/meetings/${task.meetingId}/action-items/${task.id}/status`,
        { status: newStatus }
      );
      
      const statusMessages = {
        'pending': 'Tarea marcada como pendiente',
        'in_progress': 'Tarea en proceso',
        'completed': '¡Tarea completada!'
      };
      
      toast.success(statusMessages[newStatus]);
      onTaskUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar tarea');
    }
  };

  const bgColor = type === 'pending' ? 'bg-orange-50' : 'bg-blue-50';
  const borderColor = type === 'pending' ? 'border-orange-200' : 'border-blue-200';

  return (
    <Card className={`${bgColor} border ${borderColor}`}>
      <CardHeader 
        className="pb-3 cursor-pointer select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            {title} ({tasks.length})
          </div>
          <ChevronDown 
            className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </CardTitle>
      </CardHeader>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <CardContent className="pt-0">
              {tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{emptyMessage}</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {tasks.map((task, index) => (
                    <div 
                      key={task.id || index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-white border transition-all"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-stone-700">
                          {task.description}
                        </p>
                        <Link
                          to={`/meeting/${task.meetingId}`}
                          className="text-xs text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1 mt-1"
                        >
                          <FileText className="w-3 h-3" />
                          {task.meetingTitle}
                        </Link>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {type === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                              onClick={() => updateTaskStatus(task, 'in_progress')}
                            >
                              <PlayCircle className="w-3 h-3 mr-1" />
                              Iniciar
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                              onClick={() => updateTaskStatus(task, 'completed')}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completar
                            </Button>
                          </>
                        )}
                        {type === 'in_progress' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-stone-600 hover:text-stone-700 hover:bg-stone-100"
                              onClick={() => updateTaskStatus(task, 'pending')}
                            >
                              <Circle className="w-3 h-3 mr-1" />
                              Pendiente
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                              onClick={() => updateTaskStatus(task, 'completed')}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

const Dashboard = () => {
  const { user, getPlanId, getPlanName, getMeetingLimit, getMeetingsUsed, canCreateMeeting } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { showTour, completeTour, resetTour } = useTour();

  const planId = getPlanId();
  const planName = getPlanName();
  const meetingLimit = getMeetingLimit();
  const meetingsUsed = getMeetingsUsed();
  const canCreate = canCreateMeeting();

  useEffect(() => {
    fetchMeetings();
    // Poll for updates only when not searching
    const interval = setInterval(() => {
      if (!searchQuery) fetchMeetings();
    }, 5000);
    return () => clearInterval(interval);
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredMeetings(meetings);
    }
  }, [meetings, searchQuery]);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings`);
      setMeetings(response.data);
      if (!searchQuery) {
        setFilteredMeetings(response.data);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching meetings:", err);
      setError("No se pudieron cargar las reuniones");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredMeetings(meetings);
      return;
    }
    
    if (query.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/meetings/search/${encodeURIComponent(query)}`);
      setFilteredMeetings(response.data);
    } catch (err) {
      console.error("Error searching:", err);
      // Fallback to client-side filter
      const filtered = meetings.filter(m => 
        m.title.toLowerCase().includes(query.toLowerCase()) ||
        m.description?.toLowerCase().includes(query.toLowerCase()) ||
        m.summary?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredMeetings(filtered);
    } finally {
      setSearching(false);
    }
  }, [meetings]);

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredMeetings(meetings);
  };

  const stats = {
    total: meetings.length,
    completed: meetings.filter(m => m.status === "completed").length,
    processing: meetings.filter(m => ["transcribing", "processing"].includes(m.status)).length,
    pendingTasks: meetings.reduce((acc, m) => acc + (m.action_items?.filter(item => !item.completed && (!item.status || item.status === 'pending'))?.length || 0), 0),
    inProgressTasks: meetings.reduce((acc, m) => acc + (m.action_items?.filter(item => item.status === 'in_progress')?.length || 0), 0)
  };

  // Get pending tasks (status = pending or no status)
  const pendingTasks = meetings.flatMap(meeting => 
    (meeting.action_items || [])
      .filter(item => !item.completed && (!item.status || item.status === 'pending'))
      .map(item => ({
        ...item,
        status: 'pending',
        meetingId: meeting.id,
        meetingTitle: meeting.title
      }))
  ).slice(0, 15);

  // Get in-progress tasks
  const inProgressTasks = meetings.flatMap(meeting => 
    (meeting.action_items || [])
      .filter(item => item.status === 'in_progress')
      .map(item => ({
        ...item,
        meetingId: meeting.id,
        meetingTitle: meeting.title
      }))
  ).slice(0, 15);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Product Tour */}
      {showTour && <ProductTour onComplete={completeTour} />}

      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">
              Tus Reuniones
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Graba, transcribe y genera minutas automáticas con inteligencia artificial.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/ayuda">
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                <HelpCircle className="w-4 h-4 mr-1" />
                Ayuda
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetTour}
              className="text-muted-foreground"
            >
              Ver tutorial
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10"
      >
        {/* Plan Card */}
        <Card className={`${planId === 'pro' ? 'bg-violet-50' : planId === 'basic' ? 'bg-orange-50' : 'bg-stone-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              {planId === 'pro' ? (
                <Crown className="w-5 h-5 text-violet-600" />
              ) : planId === 'basic' ? (
                <Zap className="w-5 h-5 text-orange-600" />
              ) : (
                <Sparkles className="w-5 h-5 text-stone-600" />
              )}
              <span className={`font-bold ${planId === 'pro' ? 'text-violet-700' : planId === 'basic' ? 'text-orange-600' : 'text-stone-700'}`}>
                {planName}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mb-2">
              {meetingLimit === -1 ? 'Ilimitadas' : `${meetingsUsed}/${meetingLimit} reuniones`}
            </div>
            {planId === 'free' && (
              <Link to="/pricing">
                <Button size="sm" variant="outline" className="w-full text-xs h-7">
                  Actualizar
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
        
        <Card className="bg-stone-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total reuniones</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-emerald-700">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">Completadas</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-amber-700">{stats.processing}</div>
            <div className="text-sm text-muted-foreground">Procesando</div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-orange-600">{stats.pendingTasks}</div>
            <div className="text-sm text-muted-foreground">Tareas pendientes</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-blue-600">{stats.inProgressTasks}</div>
            <div className="text-sm text-muted-foreground">Tareas en proceso</div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-8"
      >
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar reuniones..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="clear-search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-muted-foreground mt-2">
            {searching ? "Buscando..." : `${filteredMeetings.length} resultado${filteredMeetings.length !== 1 ? 's' : ''} para "${searchQuery}"`}
          </p>
        )}
      </motion.div>

      {/* Meetings Grid */}
      {filteredMeetings.length === 0 && !searchQuery ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center py-20"
        >
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-stone-100 flex items-center justify-center">
            <FileText className="w-10 h-10 text-stone-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Sin reuniones aún</h2>
          <p className="text-muted-foreground mb-6">
            Comienza grabando tu primera reunión
          </p>
          <Link to="/new">
            <Button 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="empty-state-new-meeting"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Reunión
            </Button>
          </Link>
        </motion.div>
      ) : filteredMeetings.length === 0 && searchQuery ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Search className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-xl font-semibold mb-2">Sin resultados</h3>
          <p className="text-muted-foreground">
            No se encontraron reuniones para "{searchQuery}"
          </p>
          <Button variant="outline" onClick={clearSearch} className="mt-4">
            Limpiar búsqueda
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="meetings-grid" data-tour="meetings-grid">
          {filteredMeetings.map((meeting, index) => (
            <MeetingCard key={meeting.id} meeting={meeting} index={index} />
          ))}
        </div>
      )}

      {/* Tasks Sections - At the bottom */}
      {(pendingTasks.length > 0 || inProgressTasks.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          data-tour="tasks-section"
        >
          {/* Pending Tasks */}
          <TasksSection 
            title="Tareas Pendientes"
            tasks={pendingTasks}
            type="pending"
            icon={<Circle className="w-4 h-4 text-orange-500" />}
            emptyMessage="No hay tareas pendientes"
            onTaskUpdate={fetchMeetings}
          />
          
          {/* In Progress Tasks */}
          <TasksSection 
            title="Tareas en Proceso"
            tasks={inProgressTasks}
            type="in_progress"
            icon={<PlayCircle className="w-4 h-4 text-blue-500" />}
            emptyMessage="No hay tareas en proceso"
            onTaskUpdate={fetchMeetings}
          />
        </motion.div>
      )}

      {error && (
        <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-lg text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
